import { Badge, Button, cn } from '@yeshuman/sdk'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import type { Stage } from '../../hooks/use-opportunity-applicants'

interface StageColumnHeaderProps {
  stage: Stage
  count: number
  isOver?: boolean
  className?: string
  subtitle?: string
  collapsed?: boolean
  onToggleCollapse?: () => void
  collapseDisabled?: boolean
}

export function StageColumnHeader({
  stage,
  count,
  isOver = false,
  className,
  subtitle,
  collapsed = false,
  onToggleCollapse,
  collapseDisabled = false,
}: StageColumnHeaderProps) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-between gap-2 border-b border-border/60 bg-background/80 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        isOver && 'border-foreground/40 bg-muted/60',
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {onToggleCollapse ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label={collapsed ? `Expand ${stage.name} column` : `Collapse ${stage.name} column`}
            aria-expanded={!collapsed}
            disabled={collapseDisabled}
            onClick={onToggleCollapse}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronLeft className="h-4 w-4" aria-hidden />
            )}
          </Button>
        ) : null}
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                'h-2 w-2 shrink-0 rounded-full',
                stage.is_terminal ? 'bg-muted-foreground/60' : 'bg-primary/60',
              )}
              aria-hidden
            />
            <h3 className="truncate text-sm font-semibold leading-none">{stage.name}</h3>
            {stage.is_terminal ? (
              <Badge variant="outline" className="text-xs uppercase tracking-wide">
                Terminal
              </Badge>
            ) : null}
          </div>
          {subtitle ? (
            <p className="mt-1 truncate text-xs text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
      </div>
      <Badge variant="secondary" className="shrink-0 tabular-nums">
        {count}
      </Badge>
    </div>
  )
}
